import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { compact, indexAbilities } from "./documents";
import { rpc } from "./rpc";
import type { Doc, FilterCondition, SearchResultItem } from "./types";

/**
 * `documents.search` and `documents.search_titles` (`docs/spec/search.md` §7).
 * The two are asymmetric on the wire — one returns result objects, the other
 * documents — and this module hides that behind one result shape.
 */

export type SearchSort = "relevance" | "createdAt" | "updatedAt" | "title";
export type SearchStatus = "draft" | "archived" | "published";
export type DateFilter = "day" | "week" | "month" | "year";

export interface SearchParams {
  query: string;
  collectionId?: string;
  userId?: string;
  dateFilter?: DateFilter;
  statusFilter?: SearchStatus[];
  sort?: SearchSort;
  direction?: "ASC" | "DESC";
  titlesOnly?: boolean;
  limit?: number;
  offset?: number;
}

export interface SearchResults {
  items: SearchResultItem[];
  abilities: Record<string, Record<string, boolean>>;
  total: number;
}

export const searchMethod = (titlesOnly: boolean): string =>
  titlesOnly ? "documents.search_titles" : "documents.search";

/** The relative-date duration each range chip means (`api-conventions.md` §7). */
const DURATIONS: Record<DateFilter, string> = {
  day: "-P1D",
  week: "-P1W",
  month: "-P1M",
  year: "-P1Y",
};

/**
 * The request body, split out because what a chip *sends* is the thing worth
 * asserting.
 *
 * Scope chips are expressed in the structured `filters` grammar. Status is the
 * exception: it has no filterable field — `statusFilter` replaces the server's
 * default status predicate rather than narrowing it, so it cannot be rewritten
 * as a condition — and combining `filters` with any legacy scalar is a 400
 * (`docs/spec/search.md` §8). So when a status chip is on, the scope chips fall
 * back to their legacy scalar spellings, which the spec states are translated
 * into exactly the same expression server-side.
 */
export function searchBody(params: SearchParams): Record<string, unknown> {
  const scoped =
    params.collectionId !== undefined ||
    params.userId !== undefined ||
    params.dateFilter !== undefined;
  const statuses = params.statusFilter ?? [];
  const legacy = statuses.length > 0;

  const filters: FilterCondition[] = [];
  if (!legacy && scoped) {
    if (params.collectionId !== undefined) {
      filters.push({ field: "collectionId", operator: "eq", value: params.collectionId });
    }
    if (params.userId !== undefined) {
      filters.push({ field: "userId", operator: "eq", value: params.userId });
    }
    if (params.dateFilter !== undefined) {
      filters.push({ field: "updatedAt", operator: "gte", value: DURATIONS[params.dateFilter] });
    }
  }

  return compact({
    query: params.query,
    ...(filters.length > 0 ? { filters } : {}),
    ...(legacy
      ? {
          collectionId: params.collectionId,
          userId: params.userId,
          dateFilter: params.dateFilter,
          statusFilter: statuses,
        }
      : {}),
    sort: params.sort,
    direction: params.direction,
    limit: params.limit ?? 25,
    offset: params.offset ?? 0,
  });
}

export function useSearch(params: SearchParams, enabled = true): UseQueryResult<SearchResults> {
  const titlesOnly = params.titlesOnly ?? false;
  const method = searchMethod(titlesOnly);
  const body = searchBody(params);

  return useQuery({
    queryKey: ["search", method, body],
    enabled: enabled && params.query.trim() !== "",
    retry: false,
    placeholderData: (previous: SearchResults | undefined) => previous,
    queryFn: async () => {
      const result = await rpc<SearchResultItem[] | Doc[]>(method, body);
      const items: SearchResultItem[] = result.data.map((entry) =>
        "document" in entry ? entry : { document: entry },
      );
      return {
        items,
        abilities: indexAbilities(result.policies),
        total: result.pagination?.total ?? items.length,
      };
    },
  });
}

/** Title autocomplete, used by the command palette and by `@`/`[[` mentions. */
export async function searchTitles(query: string, limit = 10): Promise<Doc[]> {
  if (query.trim() === "") return [];
  const result = await rpc<Doc[]>("documents.search_titles", { query, limit });
  return result.data;
}
